import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  CalendarPlus,
  MessageCircle,
  Pencil,
  Phone,
  PhoneCall,
  Send,
  Share2,
  ShieldAlert,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { ModalConfirmacao } from "@/shared/components/ModalConfirmacao";
import { rotuloSexo } from "@/shared/lib/sexo";
import { limparAviso, lerAviso } from "@/modules/visitantes/utils/aviso";
import { ModalCompartilharContato } from "@/modules/visitantes/components/ModalCompartilharContato";
import { ModalNovaVisita } from "@/modules/visitantes/components/ModalNovaVisita";
import { ModalPassarInformacao } from "@/modules/visitantes/components/ModalPassarInformacao";
import { ModalRegistrarContato } from "@/modules/visitantes/components/ModalRegistrarContato";
import { ModalWhatsapp } from "@/modules/visitantes/components/ModalWhatsapp";
import { SeloEstado, SeloFaixa, SeloTexto } from "@/modules/visitantes/components/Selos";
import {
  useExcluirVisitante,
  useFichaVisitante,
  useMarcarInformacoesLidas,
  usePapelVisitantes,
  usePodeInformar,
  useRegistrarConsentimento,
  useRetirarConsentimento,
} from "@/modules/visitantes/hooks/useVisitantes";
import {
  ROTULO_CATEGORIA,
  ROTULO_PARENTESCO,
  categoriaDaIdade,
  rotuloAnos,
} from "@/modules/visitantes/utils/familia";
import {
  IDADE_CRIANCA_ATE,
  ROTULO_PREFERENCIA,
  alvoDeContato,
  calcularIdade,
  estadoDe,
  formatarData,
  formatarTelefone,
  linkLigar,
  rotuloCanal,
  rotuloEstadoCivil,
  textoCompartilharContato,
  voltouAposContato,
  type CanalContato,
  type PreferenciaContato,
} from "@/modules/visitantes/utils/regras";

const BOTAO =
  "inline-flex min-h-10 items-center justify-center gap-1.5 rounded-lg border border-border bg-transparent px-3 text-[13px] font-semibold text-text transition-colors hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50";
const BOTAO_CONTATO =
  "inline-flex min-h-11 flex-1 items-center justify-center gap-2 rounded-lg bg-primary px-4 text-sm font-semibold text-on-primary transition-colors hover:bg-primary-hover";
const BOTAO_CONTATO_OFF =
  "inline-flex min-h-11 flex-1 cursor-not-allowed items-center justify-center gap-2 rounded-lg bg-surface-2 px-4 text-sm font-semibold text-muted opacity-70";

const VISITAS_VISIVEIS = 5;

/** Data (dd/mm/aaaa, horário de Brasília) de um instante guardado no banco. */
function dataDoInstante(ts: string | null | undefined): string {
  if (!ts) return "";
  return new Date(ts).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

function Secao({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="mb-4 rounded-xl border border-border bg-surface p-4">
      <h2 className="mb-2 text-sm font-bold text-text">{titulo}</h2>
      {children}
    </section>
  );
}

function Linha({ rotulo, valor }: { rotulo: string; valor: string | null | undefined }) {
  if (!valor) return null;
  return (
    <div className="flex flex-col gap-0.5 py-1.5 sm:flex-row sm:gap-3">
      <dt className="w-44 shrink-0 text-xs font-semibold text-muted">{rotulo}</dt>
      <dd className="min-w-0 whitespace-pre-line break-words text-sm text-text">{valor}</dd>
    </div>
  );
}

/** Ficha do visitante: dados, histórico de visitas, contatos e as ações de acompanhamento do papel. */
export function FichaVisitantePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { papel } = usePapelVisitantes();
  const gestao = papel === "gestao";
  const { podeInformar } = usePodeInformar();
  // Líder de outro ministério; o administrador que também lidera/gerencia um ministério age como ele.
  const lider = papel === "lider" || (papel === "admin" && podeInformar);
  const completo = gestao || papel === "admin";

  const { data: ficha, isLoading, isError } = useFichaVisitante(id);
  const excluir = useExcluirVisitante();
  const marcarLidas = useMarcarInformacoesLidas();
  const registrarConsentimento = useRegistrarConsentimento();
  const retirarConsentimento = useRetirarConsentimento();

  const [verTodas, setVerTodas] = useState(false);
  const [zap, setZap] = useState(false);
  const [compartilhar, setCompartilhar] = useState(false);
  const [contato, setContato] = useState<{ aberto: boolean; canal: CanalContato }>({
    aberto: false,
    canal: "whatsapp",
  });
  const [perguntaFalou, setPerguntaFalou] = useState<CanalContato | null>(null);
  const [novaVisita, setNovaVisita] = useState(false);
  const [passar, setPassar] = useState(false);
  const [confirmaExcluir, setConfirmaExcluir] = useState(false);
  const [erroExcluir, setErroExcluir] = useState<string | null>(null);
  const [avisouPessoa, setAvisouPessoa] = useState(false);
  const [erroConsentimento, setErroConsentimento] = useState<string | null>(null);
  const [abrirRetirada, setAbrirRetirada] = useState(false);
  const [confirmaRetirar, setConfirmaRetirar] = useState(false);
  const [aviso, setAviso] = useState<string | null>(
    (location.state as { aviso?: string } | null)?.aviso ?? lerAviso(),
  );
  useEffect(() => {
    limparAviso();
  }, []);

  // Informações de líderes: marcadas como lidas ao abrir, mas o "Nova" fica até sair da tela.
  const novasInfo = useRef<Set<string>>(new Set());
  const jaMarcou = useRef(false);
  useEffect(() => {
    if (!gestao || !ficha?.informacoes || jaMarcou.current) return;
    const naoLidas = ficha.informacoes.filter((i) => !i.lida);
    if (naoLidas.length === 0) return;
    jaMarcou.current = true;
    naoLidas.forEach((i) => novasInfo.current.add(i.id));
    marcarLidas.mutate(ficha.id);
  }, [gestao, ficha, marcarLidas]);

  const idade = ficha ? calcularIdade(ficha.nascimento) : null;
  const alvo = useMemo(
    () =>
      ficha
        ? alvoDeContato({
            nascimento: ficha.nascimento,
            telefone: ficha.telefone,
            resp_nome: ficha.resp_nome,
            resp_telefone: ficha.resp_telefone,
          })
        : null,
    [ficha],
  );

  if (isLoading) return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;
  if (isError || !ficha || !alvo) {
    return (
      <main className="mx-auto w-full max-w-2xl px-4 py-10 text-center">
        <p className="text-sm text-muted">Visitante não encontrado.</p>
        <Link
          to="/visitantes"
          className="mt-3 inline-block text-sm font-semibold text-primary underline"
        >
          Voltar para a lista
        </Link>
      </main>
    );
  }

  const estado = estadoDe({
    contatos: ficha.contatos_total,
    visitas: ficha.visitas.length,
    preferencia_contato: ficha.preferencia_contato,
  });
  const naoProcurar = ficha.preferencia_contato === "nao";
  // O administrador-líder segue a regra do líder: não age em criança nem em quem tem outra igreja.
  const visivelAoLider = ficha.igreja_situacao !== "outra" && (idade ?? 0) >= 12;
  const atuaComoLider = lider && (papel === "lider" || visivelAoLider);
  const podeContatar = (gestao || atuaComoLider) && !naoProcurar && !!alvo.telefone;
  const motivoSemContato = naoProcurar
    ? "Esta pessoa pediu para não ser procurada."
    : !alvo.telefone
      ? alvo.ehResponsavel
        ? "Sem telefone do responsável cadastrado."
        : "Sem telefone cadastrado."
      : null;
  const visitasMostradas = verTodas ? ficha.visitas : ficha.visitas.slice(0, VISITAS_VISIVEIS);
  const preferencia = ficha.preferencia_contato as PreferenciaContato | null;
  const familia = ficha.familia ?? [];
  const consent = ficha.consentimento;
  const consentRegistrado = !!consent?.em;
  const consentRetirado = !!consent?.retirado_em && !consent?.em;
  const ehCrianca = idade !== null && idade <= 11;

  async function registrarConsentimentoAgora() {
    if (!ficha) return;
    setErroConsentimento(null);
    try {
      await registrarConsentimento.mutateAsync({ id: ficha.id, versao: ficha.versao });
      setAvisouPessoa(false);
      setAviso("Consentimento registrado.");
    } catch (e) {
      setErroConsentimento(e instanceof Error ? e.message : "Não foi possível registrar.");
    }
  }

  async function retirarConsentimentoAgora() {
    if (!ficha) return;
    setErroConsentimento(null);
    try {
      await retirarConsentimento.mutateAsync({ id: ficha.id, versao: ficha.versao });
      setConfirmaRetirar(false);
      setAbrirRetirada(false);
      setAviso("Consentimento retirado. As respostas de “Sobre a pessoa” foram apagadas.");
    } catch (e) {
      setConfirmaRetirar(false);
      setErroConsentimento(e instanceof Error ? e.message : "Não foi possível retirar.");
    }
  }

  async function confirmarExcluir() {
    if (!ficha) return;
    setErroExcluir(null);
    try {
      await excluir.mutateAsync({ id: ficha.id, versao: ficha.versao });
      navigate("/visitantes", {
        replace: true,
        state: { aviso: `${ficha.nome} foi excluído(a).` },
      });
    } catch (e) {
      setErroExcluir(e instanceof Error ? e.message : "Não foi possível excluir o visitante.");
    }
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-4">
        <h1 className="break-words text-2xl font-black tracking-tight text-primary sm:text-3xl">
          {ficha.nome}
        </h1>
        <div className="mt-2 flex flex-wrap items-center gap-1.5">
          <SeloFaixa idade={idade} />
          {!(idade !== null && idade <= IDADE_CRIANCA_ATE && estado !== "contatado") && (
            <SeloEstado estado={estado} />
          )}
          {voltouAposContato({
            contatos: ficha.contatos_total,
            ultima_visita: ficha.visitas[0] ?? null,
            ultimo_contato: ficha.ultimo_contato,
          }) && <SeloTexto tom="ok">Voltou após o último contato</SeloTexto>}
          {naoProcurar && <SeloTexto tom="aviso">Prefere não ser procurado(a)</SeloTexto>}
        </div>
      </header>

      {aviso && (
        <p role="status" className="mb-3 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
          {aviso}
        </p>
      )}

      {(gestao || atuaComoLider) && (
        <Secao
          titulo={
            alvo.ehResponsavel
              ? `Contato com o responsável${alvo.nome ? ` (${alvo.nome})` : ""}`
              : "Contato"
          }
        >
          <div className="flex gap-2">
            {podeContatar && alvo.telefone ? (
              <>
                <button type="button" className={BOTAO_CONTATO} onClick={() => setZap(true)}>
                  <MessageCircle size={16} aria-hidden="true" /> WhatsApp
                </button>
                <a
                  href={linkLigar(alvo.telefone)}
                  className={BOTAO_CONTATO}
                  onClick={() => setPerguntaFalou("telefone")}
                >
                  <Phone size={16} aria-hidden="true" /> Ligar
                </a>
              </>
            ) : (
              <>
                <span className={BOTAO_CONTATO_OFF} aria-disabled="true">
                  <MessageCircle size={16} aria-hidden="true" /> WhatsApp
                </span>
                <span className={BOTAO_CONTATO_OFF} aria-disabled="true">
                  <Phone size={16} aria-hidden="true" /> Ligar
                </span>
              </>
            )}
          </div>
          {motivoSemContato && <p className="mt-2 text-xs text-muted">{motivoSemContato}</p>}

          {perguntaFalou && (
            <div role="status" className="mt-3 rounded-lg bg-surface-2 p-3">
              <p className="text-sm font-semibold text-text">Você conseguiu falar?</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button
                  type="button"
                  className={BOTAO}
                  onClick={() => {
                    setContato({ aberto: true, canal: perguntaFalou });
                    setPerguntaFalou(null);
                  }}
                >
                  Sim, registrar contato
                </button>
                <button type="button" className={BOTAO} onClick={() => setPerguntaFalou(null)}>
                  Agora não
                </button>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              className={BOTAO}
              onClick={() => setContato({ aberto: true, canal: "pessoalmente" })}
            >
              <PhoneCall size={14} aria-hidden="true" /> Registrar contato
            </button>
            {atuaComoLider && (
              <button type="button" className={BOTAO} onClick={() => setPassar(true)}>
                <Send size={14} aria-hidden="true" /> Passar informação
              </button>
            )}
            {podeContatar && (
              <button type="button" className={BOTAO} onClick={() => setCompartilhar(true)}>
                <Share2 size={14} aria-hidden="true" /> Compartilhar contato
              </button>
            )}
          </div>
        </Secao>
      )}

      {familia.length > 0 && (
        <Secao titulo="Veio com">
          <ul className="flex flex-col gap-2">
            {familia.map((f) => {
              const cat = categoriaDaIdade(f.idade);
              return (
                <li key={f.id}>
                  <Link
                    to={`/visitantes/${f.id}`}
                    className="block rounded-lg bg-surface-2 px-3 py-2 text-sm hover:bg-border"
                  >
                    <span className="font-semibold text-primary underline">{f.nome}</span>
                    <span className="mt-0.5 block text-xs text-muted">
                      {[
                        f.parentesco ? ROTULO_PARENTESCO[f.parentesco] : null,
                        cat && f.idade !== null
                          ? `${ROTULO_CATEGORIA[cat]} (${rotuloAnos(f.idade)})`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </span>
                  </Link>
                </li>
              );
            })}
          </ul>
          <p className="mt-2 text-xs text-muted">
            Cada pessoa tem a própria ficha. Quem você não pode ver (por exemplo, criança para líder
            de outro ministério) não aparece aqui.
          </p>
        </Secao>
      )}

      <Secao titulo="Dados">
        <dl className="divide-y divide-border">
          <Linha rotulo="Sexo" valor={rotuloSexo(ficha.sexo) ?? "Não informado"} />
          <Linha
            rotulo="Telefone"
            valor={ficha.telefone ? formatarTelefone(ficha.telefone) : null}
          />
          <Linha rotulo="Estado civil" valor={rotuloEstadoCivil(ficha.estado_civil)} />
          <Linha rotulo="E-mail" valor={ficha.email} />
          <Linha
            rotulo="Nascimento"
            valor={ficha.nascimento ? formatarData(ficha.nascimento) : null}
          />
          {completo && (
            <>
              <Linha rotulo="Responsável" valor={ficha.resp_nome} />
              <Linha
                rotulo="Telefone do responsável"
                valor={ficha.resp_telefone ? formatarTelefone(ficha.resp_telefone) : null}
              />
              <Linha
                rotulo="Frequenta outra igreja?"
                valor={
                  ficha.igreja_situacao === "outra"
                    ? `Sim${ficha.igreja_nome ? `: ${ficha.igreja_nome}` : ""}`
                    : ficha.igreja_situacao === "sem"
                      ? "Não frequenta nenhuma"
                      : null
                }
              />
            </>
          )}
          <Linha rotulo="Cidade" valor={ficha.cidade} />
          {completo && (
            <>
              <Linha rotulo="Rua e número" valor={ficha.endereco} />
              <Linha rotulo="Bairro" valor={ficha.bairro} />
            </>
          )}
          <Linha
            rotulo="Prefere ser procurado(a)"
            valor={preferencia ? ROTULO_PREFERENCIA[preferencia] : null}
          />
          {completo && <Linha rotulo="Pedido de oração" valor={ficha.pedido_oracao} />}
          <Linha rotulo="Observação" valor={ficha.observacao} />
        </dl>
      </Secao>

      <Secao titulo={`Visitas (${ficha.visitas.length})`}>
        {ficha.visitas.length === 0 ? (
          <p className="text-sm text-muted">Nenhuma visita registrada.</p>
        ) : (
          <ul className="flex flex-col gap-1 text-sm text-text">
            {visitasMostradas.map((d) => (
              <li key={d}>{formatarData(d)}</li>
            ))}
          </ul>
        )}
        {ficha.visitas.length > VISITAS_VISIVEIS && (
          <button
            type="button"
            onClick={() => setVerTodas((v) => !v)}
            className="mt-2 text-[13px] font-semibold text-primary underline"
          >
            {verTodas ? "Ver menos" : `Ver todas as ${ficha.visitas.length} visitas`}
          </button>
        )}
      </Secao>

      <Secao titulo={`Contatos feitos (${ficha.contatos_total})`}>
        {ficha.contatos.length === 0 ? (
          <p className="text-sm text-muted">Ninguém falou com esta pessoa ainda.</p>
        ) : (
          <ul className="flex flex-col gap-2">
            {ficha.contatos.map((c, i) => (
              <li
                key={c.id ?? `${c.data}-${i}`}
                className="rounded-lg bg-surface-2 px-3 py-2 text-sm"
              >
                <p className="font-semibold text-text">
                  {formatarData(c.data)} · {rotuloCanal(c.canal)}
                  {c.por ? ` · ${c.por}` : ""}
                </p>
                {c.observacao && (
                  <p className="mt-0.5 whitespace-pre-line text-text">{c.observacao}</p>
                )}
              </li>
            ))}
          </ul>
        )}
      </Secao>

      {completo && ficha.informacoes && ficha.informacoes.length > 0 && (
        <Secao titulo="Informações de líderes">
          <ul className="flex flex-col gap-2">
            {ficha.informacoes.map((i) => (
              <li key={i.id} className="rounded-lg bg-surface-2 px-3 py-2 text-sm">
                <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-muted">
                  {i.de ?? "—"}
                  {i.ministerio ? ` (${i.ministerio})` : ""} · {formatarData(i.em)}
                  {novasInfo.current.has(i.id) && <SeloTexto tom="aviso">Nova</SeloTexto>}
                </p>
                <p className="mt-0.5 whitespace-pre-line text-text">{i.texto}</p>
              </li>
            ))}
          </ul>
        </Secao>
      )}

      {completo && consent && (
        <Secao titulo="Consentimento">
          {consentRegistrado && (
            <>
              <p className="flex items-start gap-2 text-sm text-text">
                <ShieldCheck
                  size={16}
                  className="mt-0.5 shrink-0 text-success"
                  aria-hidden="true"
                />
                <span>
                  <strong>Consentimento registrado.</strong> Em {dataDoInstante(consent.em)}
                  {consent.por ? `, por ${consent.por}` : ""}.
                </span>
              </p>
              {gestao && (
                <div className="mt-3">
                  <button
                    type="button"
                    aria-expanded={abrirRetirada}
                    onClick={() => setAbrirRetirada((v) => !v)}
                    className="text-[13px] font-semibold text-primary underline"
                  >
                    {ehCrianca
                      ? "O responsável quer retirar o consentimento"
                      : "A pessoa quer retirar o consentimento"}
                  </button>
                  {abrirRetirada && (
                    <div className="mt-2 rounded-lg bg-surface-2 p-3 text-sm text-text">
                      <p>
                        Retirar o consentimento{" "}
                        <strong>apaga as respostas de “Sobre a pessoa”</strong> (outra igreja,
                        cidade, endereço, pedido de oração e preferência de visita) e o cadastro
                        continua com nome, contato e visitas.
                      </p>
                      <p className="mt-2 text-xs text-muted">
                        Se {ehCrianca ? "o responsável" : "a pessoa"} quiser{" "}
                        <strong>sair de vez</strong>, use “Excluir” no fim da ficha.
                      </p>
                      <button
                        type="button"
                        className={`${BOTAO} mt-3 border-danger/40 text-danger hover:bg-danger/10`}
                        onClick={() => setConfirmaRetirar(true)}
                      >
                        Retirar consentimento
                      </button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}

          {consentRetirado && (
            <p className="flex items-start gap-2 text-sm text-text">
              <ShieldAlert size={16} className="mt-0.5 shrink-0 text-warning" aria-hidden="true" />
              <span>
                <strong>Consentimento retirado.</strong> Em {dataDoInstante(consent.retirado_em)}
                {consent.retirado_por ? `, por ${consent.retirado_por}` : ""}. As respostas de
                “Sobre a pessoa” foram apagadas e não voltam a ser guardadas.
              </span>
            </p>
          )}

          {!consentRegistrado && !consentRetirado && (
            <div className="rounded-lg border border-warning/40 bg-warning/10 p-3 text-sm text-text">
              <p className="flex items-start gap-2">
                <ShieldAlert
                  size={16}
                  className="mt-0.5 shrink-0 text-warning"
                  aria-hidden="true"
                />
                <span>
                  <strong>Sem registro de consentimento.</strong> Este cadastro foi feito antes do
                  registro de consentimento. Quando falar com{" "}
                  {ehCrianca ? "o responsável" : "a pessoa"}, avise e registre.
                </span>
              </p>
            </div>
          )}

          {gestao && !consentRegistrado && (
            <div className="mt-3 flex flex-col gap-2">
              {consentRetirado && (
                <p className="text-xs text-muted">
                  Se {ehCrianca ? "o responsável" : "a pessoa"} voltar a concordar, avise de novo e
                  registre o consentimento.
                </p>
              )}
              <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-semibold text-text">
                <input
                  type="checkbox"
                  checked={avisouPessoa}
                  onChange={(e) => setAvisouPessoa(e.target.checked)}
                  className="h-5 w-5 shrink-0 accent-primary"
                />
                {ehCrianca
                  ? "Avisei o responsável e ele concordou."
                  : "Avisei a pessoa e ela concordou."}
              </label>
              <div>
                <button
                  type="button"
                  className={BOTAO}
                  disabled={!avisouPessoa || registrarConsentimento.isPending}
                  onClick={registrarConsentimentoAgora}
                >
                  Registrar consentimento
                </button>
              </div>
            </div>
          )}
          {erroConsentimento && (
            <p role="alert" className="mt-2 text-xs text-danger">
              {erroConsentimento}
            </p>
          )}
        </Secao>
      )}

      {gestao && (
        <div className="flex flex-wrap gap-2">
          <Link to={`/visitantes/${ficha.id}/editar`} className={BOTAO}>
            <Pencil size={14} aria-hidden="true" /> Editar
          </Link>
          <button type="button" className={BOTAO} onClick={() => setNovaVisita(true)}>
            <CalendarPlus size={14} aria-hidden="true" /> Nova visita
          </button>
          <button
            type="button"
            className={`${BOTAO} border-danger/40 text-danger hover:bg-danger/10`}
            onClick={() => {
              setErroExcluir(null);
              setConfirmaExcluir(true);
            }}
          >
            <Trash2 size={14} aria-hidden="true" /> Excluir
          </button>
        </div>
      )}

      {alvo.telefone && (
        <ModalWhatsapp
          aberto={zap}
          nomeDestino={alvo.ehResponsavel ? (alvo.nome ?? "responsável") : ficha.nome}
          nomeVisitante={alvo.ehResponsavel ? ficha.nome : undefined}
          telefone={alvo.telefone}
          onClose={() => setZap(false)}
          onAbriu={() => setPerguntaFalou("whatsapp")}
        />
      )}
      {alvo.telefone && (
        <ModalCompartilharContato
          aberto={compartilhar}
          nome={ficha.nome}
          textoInicial={textoCompartilharContato({
            nome: ficha.nome,
            idade,
            nascimento: ficha.nascimento,
            telefone: ficha.telefone,
            email: ficha.email,
            estadoCivil: ficha.estado_civil,
            respNome: ficha.resp_nome,
            respTelefone: ficha.resp_telefone,
            igrejaSituacao: ficha.igreja_situacao,
            igrejaNome: ficha.igreja_nome,
            cidade: ficha.cidade,
            bairro: ficha.bairro,
            endereco: ficha.endereco,
            preferenciaContato: ficha.preferencia_contato,
            pedidoOracao: ficha.pedido_oracao,
            observacao: ficha.observacao,
            visitas: ficha.visitas,
            veioCom: familia.map((f) => {
              const rel = f.parentesco ? ROTULO_PARENTESCO[f.parentesco] : null;
              return rel ? `${f.nome} (${rel})` : f.nome;
            }),
          })}
          onClose={() => setCompartilhar(false)}
        />
      )}
      <ModalRegistrarContato
        visitante={contato.aberto ? { id: ficha.id, nome: ficha.nome } : null}
        canalInicial={contato.canal}
        onClose={() => setContato((c) => ({ ...c, aberto: false }))}
        onRegistrado={() => setAviso("Contato registrado.")}
      />
      <ModalNovaVisita
        visitante={novaVisita ? { id: ficha.id, nome: ficha.nome } : null}
        onClose={() => setNovaVisita(false)}
        onRegistrada={(nome) => setAviso(`Visita de ${nome} registrada.`)}
      />
      <ModalPassarInformacao
        visitante={passar ? { id: ficha.id, nome: ficha.nome } : null}
        onClose={() => setPassar(false)}
        onEnviada={() => setAviso("Informação enviada ao ministério responsável.")}
      />
      <ModalConfirmacao
        aberto={confirmaRetirar}
        titulo="Retirar o consentimento?"
        mensagem={`Isso apaga de vez as respostas de “Sobre a pessoa” de ${ficha.nome} (outra igreja, cidade, endereço, pedido de oração e preferência de visita). Nome, contato e visitas continuam. Não dá para desfazer.`}
        textoBotaoConfirmar="Retirar consentimento"
        carregando={retirarConsentimento.isPending}
        erro={null}
        onConfirmar={retirarConsentimentoAgora}
        onCancelar={() => setConfirmaRetirar(false)}
      />
      <ModalConfirmacao
        aberto={confirmaExcluir}
        titulo="Excluir este visitante?"
        mensagem={`Isso apaga de vez o cadastro de ${ficha.nome}, com as visitas, os contatos e as informações. Não dá para desfazer. Use quando a própria pessoa pedir ou em último caso.`}
        textoBotaoConfirmar="Excluir"
        carregando={excluir.isPending}
        erro={erroExcluir}
        onConfirmar={confirmarExcluir}
        onCancelar={() => setConfirmaExcluir(false)}
      />
    </main>
  );
}
